import {memo, useMemo, useContext, Fragment} from 'react';
import PropTypes from 'prop-types';

import {Box, Image as BaseImage, Skeleton, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import Link from '@salesforce/retail-react-app/app/components/link';

import {IconSetSectionContext} from '@amblique/king-living/app/contentful/contexts/icon-set-section-context';

/** Unicode circled Latin small letter f (ⓕ). In Value Prop we replace with custom circled "f" symbol (Figma 11830-62129). */
const CIRCLED_F = '\u24D5';

/**
 * @override
 * Icon component (CF-054: Icon with Text – image/svg, title, description).
 * Inside IconSet explainer blocks uses variant 'valueProp'. Description "FreeFormⓕ" renders ⓕ as custom superscript symbol (Figma 11830-62129).
 *
 * @param {Object} props - Component props
 * @param {Contentful.Icon} props.component - Icon component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement|null}
 */
const IconComponent = ({component, isComplete = true, isFetching = false, className, ...props}) => {
  const section = useContext(IconSetSectionContext);
  const variant = section === 'valueProp' ? 'valueProp' : undefined;
  const styles = useMultiStyleConfig('contentful/icon', {component, isComplete, isFetching, variant});
  const rootClassName = [className, 'icon'].filter(Boolean).join(' ');

  const {name, title, svgCode, iconImage, description, link} = component || {};
  const displayTitle = title || name;

  const iconImageUrl = iconImage?.url;
  const iconImageTitle = iconImage?.title;

  const linkUrl = link?.url || null;
  const linkOpenInNewTab = link?.openInNewTab;
  const linkTarget = linkOpenInNewTab ? '_blank' : '_self';

  const hasContent = useMemo(() => {
    return Boolean(displayTitle || iconImageUrl || svgCode || description);
  }, [displayTitle, iconImageUrl, svgCode, description]);

  const ariaLabel = useMemo(() => {
    if (displayTitle) return displayTitle;
    if (description) return description;
    if (iconImageTitle) return iconImageTitle;
    return 'Icon';
  }, [displayTitle, description, iconImageTitle]);

  if (!isComplete && isFetching) {
    return (
      <Box {...styles.root} {...props} className={rootClassName}>
        <Skeleton height="64px" width="64px" />
        <Skeleton height="16px" mb={2} width="80px" />
        <Skeleton height="14px" width="120px" />
      </Box>
    );
  }

  if (!hasContent) {
    return null;
  }

  const iconContent = (() => {
    if (iconImageUrl) {
      return (
        <BaseImage
          alt={iconImageTitle || displayTitle || ''}
          src={iconImageUrl}
          {...styles.icon}
          className="icon__image"
        />
      );
    }

    if (svgCode) {
      return (
        <Box
          {...styles.icon}
          className="icon__image"
          dangerouslySetInnerHTML={{__html: svgCode}}
        />
      );
    }

    return null;
  })();

  const content = (
    <Box {...styles.root} {...props} aria-label={ariaLabel} className={rootClassName} role="img">
      {iconContent}
      {displayTitle && (
        <Text {...styles.title} className="icon__title">
          {displayTitle}
        </Text>
      )}
      {description &&
        (section === 'valueProp' &&
        styles.freeformSymbol &&
        description.includes(CIRCLED_F) ? (
          <Text {...styles.description} className="icon__description">
            {description.split(CIRCLED_F).map((part, i, arr) => (
              <Fragment key={i}>
                {part}
                {i < arr.length - 1 && (
                  <Box as="span" {...styles.freeformSymbol} aria-hidden>
                    f
                  </Box>
                )}
              </Fragment>
            ))}
          </Text>
        ) : (
          <Text {...styles.description} className="icon__description">{description}</Text>
        ))}
    </Box>
  );

  if (linkUrl) {
    const linkAriaLabel = link?.text || ariaLabel;

    return (
      <Box {...styles.linkWrapper} className="icon__link-wrapper">
        {/* Currently, we use Link component from @salesforce/retail-react-app/app/components/link
          due to Contentful Link component has not been implemented yet*/}
        <Link
          aria-label={linkAriaLabel}
          className="icon__link"
          target={linkTarget}
          to={linkUrl}
          {...styles.link}
        >
          {content}
        </Link>
      </Box>
    );
  }

  return content;
};

IconComponent.displayName = 'Icon';
IconComponent.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};

export const Icon = memo(IconComponent, (prevProps, nextProps) => {
  return (
    prevProps.component === nextProps.component &&
    prevProps.isComplete === nextProps.isComplete &&
    prevProps.isFetching === nextProps.isFetching
  );
});
